// Paleta Tailwind preestablecida para estados configurables.
// Las clases se listan como strings literales para que Tailwind las detecte
// estáticamente y no las purge del bundle.

export interface StatusColorPaletteEntry {
  token: string
  label: string
  classes: string
  swatchClass: string
}

export const STATUS_COLOR_PALETTE: StatusColorPaletteEntry[] = [
  { token: 'gray',    label: 'Gris',      classes: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',         swatchClass: 'bg-gray-400' },
  { token: 'red',     label: 'Rojo',      classes: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',             swatchClass: 'bg-red-400' },
  { token: 'orange',  label: 'Naranja',   classes: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300', swatchClass: 'bg-orange-400' },
  { token: 'amber',   label: 'Ámbar',     classes: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',     swatchClass: 'bg-amber-400' },
  { token: 'yellow',  label: 'Amarillo',  classes: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300', swatchClass: 'bg-yellow-400' },
  { token: 'lime',    label: 'Lima',      classes: 'bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-300',         swatchClass: 'bg-lime-400' },
  { token: 'green',   label: 'Verde',     classes: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',     swatchClass: 'bg-green-400' },
  { token: 'emerald', label: 'Esmeralda', classes: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300', swatchClass: 'bg-emerald-400' },
  { token: 'teal',    label: 'Teal',      classes: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300',         swatchClass: 'bg-teal-400' },
  { token: 'cyan',    label: 'Cian',      classes: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',         swatchClass: 'bg-cyan-400' },
  { token: 'sky',     label: 'Cielo',     classes: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-300',             swatchClass: 'bg-sky-400' },
  { token: 'blue',    label: 'Azul',      classes: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',         swatchClass: 'bg-blue-400' },
  { token: 'indigo',  label: 'Índigo',    classes: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300', swatchClass: 'bg-indigo-400' },
  { token: 'violet',  label: 'Violeta',   classes: 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-300', swatchClass: 'bg-violet-400' },
  { token: 'purple',  label: 'Púrpura',   classes: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300', swatchClass: 'bg-purple-400' },
  { token: 'pink',    label: 'Rosa',      classes: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300',         swatchClass: 'bg-pink-400' },
  { token: 'rose',    label: 'Rosado',    classes: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-300',         swatchClass: 'bg-rose-400' },
]

export const DEFAULT_STATUS_COLOR_CLASSES =
  'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'

const PALETTE_BY_TOKEN: Record<string, StatusColorPaletteEntry> = Object.fromEntries(
  STATUS_COLOR_PALETTE.map(p => [p.token, p])
)

export function isHexColor(v?: string): boolean {
  return typeof v === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v)
}

export function isPaletteToken(v?: string): boolean {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(PALETTE_BY_TOKEN, v)
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  let h = hex.replace('#', '')
  if (h.length === 3) h = h.split('').map(c => c + c).join('')
  const n = parseInt(h, 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

// Contrast text color (black or white) using WCAG relative luminance.
function pickTextColorForHex(hex: string): string {
  const { r, g, b } = hexToRgb(hex)
  const srgb = [r, g, b].map(v => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  const L = 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2]
  return L > 0.5 ? '#111827' : '#ffffff'
}

export type StatusColorStyle =
  | { mode: 'classes'; classes: string }
  | { mode: 'style'; style: Record<string, string> }

export function getStatusColorStyle(color?: string): StatusColorStyle {
  if (isPaletteToken(color)) {
    return { mode: 'classes', classes: PALETTE_BY_TOKEN[color as string].classes }
  }
  if (isHexColor(color)) {
    return {
      mode: 'style',
      style: {
        backgroundColor: color as string,
        color: pickTextColorForHex(color as string),
      },
    }
  }
  return { mode: 'classes', classes: DEFAULT_STATUS_COLOR_CLASSES }
}

export type StatusSwatch =
  | { mode: 'class'; class: string }
  | { mode: 'style'; style: Record<string, string> }

// Muestra (dot) de color sólido para el filtro por color.
// Token de paleta → swatchClass (bg-*-400); hex → color directo; sin color → gris.
export function getStatusSwatch(color?: string): StatusSwatch {
  if (isPaletteToken(color)) {
    return { mode: 'class', class: PALETTE_BY_TOKEN[color as string].swatchClass }
  }
  if (isHexColor(color)) {
    return { mode: 'style', style: { backgroundColor: color as string } }
  }
  return { mode: 'class', class: 'bg-gray-400' }
}

// Etiqueta legible del color para tooltips del filtro por color.
// `noColorLabel` se usa cuando el estado no tiene color configurado.
export function getStatusColorLabel(color: string | undefined, noColorLabel: string): string {
  if (isPaletteToken(color)) return PALETTE_BY_TOKEN[color as string].label
  if (isHexColor(color)) return color as string
  return noColorLabel
}

// Clave estable para agrupar estados por color (hex normalizado a minúsculas).
export const NO_COLOR_KEY = '__none'
export function statusColorKey(color?: string): string {
  if (isPaletteToken(color)) return color as string
  if (isHexColor(color)) return (color as string).toLowerCase()
  return NO_COLOR_KEY
}
